import { useState, useEffect, useRef } from 'react'
import { aiApi } from '@/lib/api'
import { Loader2, Sparkles, X, Plus } from 'lucide-react'

export function EditableNote({ noteKey, value, onChange, placeholder }: {
  noteKey: string; value: string; onChange: (key: string, val: string) => void; placeholder?: string
}) {
  const [text, setText] = useState(value)
  const [isFixing, setIsFixing] = useState(false)
  const [showEditor, setShowEditor] = useState(!!value)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { setText(value) }, [value])

  const handleBlur = () => { if (text !== value) onChange(noteKey, text) }

  const handleFix = async () => {
    if (!text.trim()) return
    setIsFixing(true)
    try {
      const result = await aiApi.fixText(text)
      if (result?.fixed_text) { setText(result.fixed_text); onChange(noteKey, result.fixed_text) }
    } catch { /* ignore */ } finally { setIsFixing(false) }
  }

  if (!showEditor) {
    return (
      <button
        onClick={() => { setShowEditor(true); setTimeout(() => textareaRef.current?.focus(), 50) }}
        className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 mt-1"
      >
        <Plus className="w-3 h-3" /> Add Note
      </button>
    )
  }

  return (
    <div className="mt-2 relative">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || 'Add a note...'}
        rows={2}
        className="w-full text-sm p-3 pr-20 rounded border border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y"
      />
      <div className="absolute top-2 right-2 flex items-center gap-1">
        {text.trim() && (
          <button onClick={handleFix} disabled={isFixing} title="AI Fix spelling & grammar"
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors disabled:opacity-50">
            {isFixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Fix
          </button>
        )}
        {!text.trim() && (
          <button onClick={() => setShowEditor(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
        )}
      </div>
    </div>
  )
}
